import { useState, useMemo, useEffect } from 'react';
import { Modal, Stack, TextInput, Checkbox, Button, Group, Text, ScrollArea, Loader, Center } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import fuzzysort from 'fuzzysort';
import { useStreams } from '@/features/streams/useStreams';
import type { CPUserMediaData } from '@/lib/types/Auth';

interface StreamPickerModalProps {
  opened: boolean;
  onClose: () => void;
  cpuserId: number;
  selectedMediaIds: number[];
  onSave: (medias: CPUserMediaData[]) => void;
}

export function StreamPickerModal({
  opened,
  onClose,
  cpuserId,
  selectedMediaIds,
  onSave,
}: StreamPickerModalProps) {
  const { streams, isLoading: isStreamsLoading } = useStreams();
  const [selected, setSelected] = useState<Set<number>>(new Set(selectedMediaIds));
  const [search, setSearch] = useState('');

  // Reset selection when modal opens
  useEffect(() => {
    if (opened) {
      setSelected(new Set(selectedMediaIds));
      setSearch('');
    }
  }, [opened, selectedMediaIds]);

  const streamOptions = useMemo(() => {
    return streams
      .map(s => ({ mediaId: s.media.mediaId, title: s.media.title }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [streams]);

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return streamOptions;
    const results = fuzzysort.go(search, streamOptions, { key: 'title', threshold: -1000 });
    return results.map(r => r.obj);
  }, [streamOptions, search]);

  function toggleStream(mediaId: number) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(mediaId)) {
        next.delete(mediaId);
      } else {
        next.add(mediaId);
      }
      return next;
    });
  }

  function handleSave() {
    const medias: CPUserMediaData[] = Array.from(selected).map(mediaId => ({
      cpuserId,
      mediaId,
    }));
    onSave(medias);
    onClose();
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Select Streams"
      size="md"
    >
      <Stack gap="md">
        <TextInput
          placeholder="Search streams..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          aria-label="Search streams"
        />

        {isStreamsLoading ? (
          <Center py="lg">
            <Loader size="sm" />
          </Center>
        ) : filteredOptions.length === 0 ? (
          <Text c="dimmed" size="sm" ta="center" py="md">
            {streams.length === 0 ? 'No streams available.' : 'No streams match your search.'}
          </Text>
        ) : (
          <ScrollArea h={300}>
            <Stack gap="xs">
              {filteredOptions.map(option => (
                <Checkbox
                  key={option.mediaId}
                  label={option.title}
                  checked={selected.has(option.mediaId)}
                  onChange={() => toggleStream(option.mediaId)}
                />
              ))}
            </Stack>
          </ScrollArea>
        )}

        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            {selected.size} stream{selected.size !== 1 ? 's' : ''} selected
          </Text>
          <Group gap="sm">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave}>Save Selection</Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
